import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { apiClient, ApiHttpError } from "../../lib/api-client";
import { useAuth } from "../../lib/auth-context";
import { formatDate } from "../../lib/format";
import { toast } from "../../lib/toast";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

/**
 * Asks the club owner to accept the data processing agreement (Art. 28 GDPR) until
 * they do. Only the owner can bind the club, so nobody else sees it. Until it is
 * accepted the club's public registration page stays closed (the server enforces
 * that); "Later" only hides this dialog until the next visit.
 */
export function DpaGate() {
  const { user, refreshUser } = useAuth();
  const { t } = useTranslation("legal");
  const [dismissed, setDismissed] = useState(false);
  const [role, setRole] = useState("");
  const [authorised, setAuthorised] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dpa = user?.club?.dpa;
  const open = Boolean(user?.isOwner && !user.impersonator && !user.accessGrant && dpa && !dpa.accepted && !dismissed);
  if (!open || !user?.club || !dpa) return null;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (role.trim().length < 3) return setError(t("gate.errors.role"));
    if (!authorised) return setError(t("gate.errors.authorised"));

    setError(null);
    setBusy(true);
    try {
      await apiClient.acceptClubDpa(user.club!.id, { version: dpa.version, role: role.trim(), authorised: true });
      toast.success(t("gate.accepted"));
      await refreshUser();
    } catch (err) {
      if (err instanceof ApiHttpError && err.status === 409) {
        // The agreement changed while this page was open: reload to show the new version.
        toast.error(t("gate.versionChanged"));
        await refreshUser();
      } else {
        setError(err instanceof ApiHttpError && err.fieldErrors.role ? err.fieldErrors.role : t("gate.errors.generic"));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(next) => !next && setDismissed(true)}>
      <DialogContent>
        <form onSubmit={submit} className="grid gap-4">
          <DialogHeader>
            <DialogTitle>{t("gate.title")}</DialogTitle>
            <DialogDescription>{t("gate.description")}</DialogDescription>
          </DialogHeader>

          <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
            {(t("gate.summary", { returnObjects: true }) as unknown as string[]).map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
          <a href="/dpa" target="_blank" rel="noreferrer" className="text-sm underline underline-offset-2">
            {t("gate.read")}
          </a>

          <div className="grid gap-1.5">
            <Label htmlFor="dpa-role">{t("gate.roleLabel")}</Label>
            <Input id="dpa-role" value={role} maxLength={100} placeholder={t("gate.rolePlaceholder")} onChange={(e) => setRole(e.target.value)} />
          </div>

          <div className="flex items-start gap-2">
            <Checkbox id="dpa-authorised" checked={authorised} onCheckedChange={(value) => setAuthorised(value === true)} className="mt-0.5" />
            <Label htmlFor="dpa-authorised" className="text-sm font-normal leading-snug">
              {t("gate.authorised", { date: formatDate(dpa.version) })}
            </Label>
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setDismissed(true)} disabled={busy}>
              {t("gate.later")}
            </Button>
            <Button type="submit" disabled={busy}>
              {busy && <Loader2 className="mr-2 size-4 animate-spin" aria-hidden />}
              {t("gate.accept")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
